import { useState, useEffect } from 'react'
import { useAuth } from './hooks/useAuth'
import { LoadingSpinner, ErrorDisplay } from '../../shared/components'

export default function LoginPage() {
  const { signIn, isLoading, error, clearError } = useAuth()
  const [email, setEmail] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [emailError, setEmailError] = useState('')

  // Clear errors when component mounts
  useEffect(() => {
    clearError()
  }, [clearError])

  // Clear email errors when email changes (but not auth context errors)
  useEffect(() => {
    setEmailError('')
  }, [email])

  // Show "Email is required" error when email is empty and user has interacted
  const [hasInteracted, setHasInteracted] = useState(false)
  
  useEffect(() => {
    if (hasInteracted && !email.trim()) {
      setEmailError('Email is required')
    }
  }, [email, hasInteracted])

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Clear previous errors
    setEmailError('')
    clearError()

    // Validate email format
    if (!email.trim()) {
      setEmailError('Email is required')
      return
    }

    if (!validateEmail(email.trim())) {
      setEmailError('Please enter a valid email address')
      return
    }

    try {
      await signIn(email.trim())
      setEmailSent(true)
    } catch (error) {
      // Error is handled by the auth context
      setEmailSent(false)
    }
  }



  const handleTryAgain = () => {
    setEmailSent(false)
    setEmail('')
    clearError()
    setEmailError('')
  }

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '1rem'
  }

  const cardStyle = {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '2rem',
    width: '100%',
    maxWidth: '400px'
  }

  const headerStyle = {
    textAlign: 'center' as const,
    marginBottom: '2rem'
  }

  const formStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem'
  }

  const inputStyle = {
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
    transition: 'border-color 0.2s'
  }

  const inputErrorStyle = {
    ...inputStyle,
    borderColor: '#dc3545'
  }

  const buttonStyle = {
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem'
  }

  const secondaryButtonStyle = {
    backgroundColor: 'transparent',
    color: '#007bff',
    border: '1px solid #007bff',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }

  if (emailSent) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={headerStyle}>
            <h1>Check Your Email</h1>
          </div>
          
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '3rem', color: '#28a745', marginBottom: '1rem' }}>✓</div>
            <p style={{ margin: '0.5rem 0', color: '#333' }}>
              We've sent a magic link to <strong>{email}</strong>
            </p>
            <p style={{ color: '#666', fontSize: '0.9rem', margin: '0.5rem 0' }}>
              Click the link in your email to sign in. The link will expire in 15 minutes.
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button 
              type="button" 
              onClick={handleTryAgain}
              style={secondaryButtonStyle}
            >
              Try Different Email
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h1 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.5rem' }}>
            Sign In to Stash Dash
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            Enter your email to receive a magic link
          </p>
        </div>

        <form onSubmit={handleSubmit} style={formStyle} noValidate>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label htmlFor="email" style={{ fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setHasInteracted(true)}
              placeholder="Enter your email"
              disabled={isLoading}
              style={emailError ? inputErrorStyle : inputStyle}
              autoComplete="email"
              autoFocus
            />
            {emailError && (
              <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                {emailError}
              </div>
            )}
          </div>

          {error && (
            <ErrorDisplay error={error} />
          )}

          <button 
            type="submit" 
            disabled={isLoading || !email.trim()}
            style={{
              ...buttonStyle,
              backgroundColor: isLoading || !email.trim() ? '#6c757d' : '#007bff',
              cursor: isLoading || !email.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="small" />
                Sending Magic Link...
              </>
            ) : (
              'Send Magic Link'
            )}
          </button>
        </form>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.8rem' }}>
            Only authorized email addresses can access this application.
          </p>
        </div>
      </div>
    </div>
  )
}